import { createContext, useContext, useEffect, type Dispatch, type SetStateAction } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { RouteTypes } from '@energovizor/api-client';
import userSelector from '@/store/selectors/userSelector';
import { useGetActiveMunicipality } from './getMunicipality';
import { apiClient } from './apiClient';
import type { ChartDateRange } from './chartDateRange';
import { solarDate } from '@/components/solar/solarSeriesModel';

export type MissingDataAlert = NonNullable<RouteTypes['/buildings/missing-data-alerts']['get']['response']['payload']>[number];
export type MissingDataPeriodScope = { owner: symbol; range: ChartDateRange | null };
export const MissingDataPeriodContext = createContext<{ scopes: MissingDataPeriodScope[]; setScopes: Dispatch<SetStateAction<MissingDataPeriodScope[]>> } | null>(null);

// Share the visible chart/dashboard period with the header, reverting to this year elsewhere.
export function useMissingDataPeriod(range: ChartDateRange | null) {
  const setScopes = useContext(MissingDataPeriodContext)?.setScopes;
  const from = range?.from;
  const to = range?.to;
  useEffect(() => {
    const owner = Symbol();
    setScopes?.(current => [...current, { owner, range: from && to ? { from, to } : null }]);
    return () => setScopes?.(current => current.filter(scope => scope.owner !== owner));
  }, [from, to, setScopes]);
}

export function useMissingDataHeaderRange(): ChartDateRange | null {
  const context = useContext(MissingDataPeriodContext);
  const scope = context?.scopes[context.scopes.length - 1];
  const today = solarDate(new Date());
  return scope ? scope.range : { from: today.startOf('year').format('YYYY-MM-DD'), to: today.endOf('year').format('YYYY-MM-DD') };
}

export function useGetMissingDataAlerts(range: ChartDateRange | null, enabled = true) {
  const user = useSelector(userSelector);
  const municipalityId = useGetActiveMunicipality({ enabled });
  const today = solarDate(new Date()).format('YYYY-MM-DD');
  return useQuery({
    queryKey: ['missing-data-alerts', municipalityId, user?.id, range?.from, range?.to, today],
    enabled: enabled && !!range && !!municipalityId && user?.type === 'client' && !!user.verifiedEmail && !!user.verifiedByAdmin,
    queryFn: async () => {
      if (!range) return [];
      const response = await apiClient.get('/buildings/missing-data-alerts', { params: range });
      return response.data.payload ?? [];
    },
  });
}

export const missingDataPeriodLabel = (period: ChartDateRange) => `${solarDate(period.from).format('D. M. YYYY')} – ${solarDate(period.to).format('D. M. YYYY')}`;
export const missingDataMessage = (period: ChartDateRange) => `Chybí vyúčtování nebo odečet za období ${missingDataPeriodLabel(period)}.`;
